import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  ARCH_NAME,
  KIND_NAME,
  PLATFORM_NAME,
  filesFor,
  mb,
  useDetectedSystem,
  useDownloads,
} from "../downloads";
import type { Browser } from "../downloads";
import { writeClipboard } from "../components/CopySettings";
import { useInstallState } from "../install";
import { Alert, Card, PageHead } from "../shell/ui";

function CopyHash({ hash }: { hash: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="btn btn--sm btn--ghost mono"
      title={`SHA-256: ${hash} (click to copy)`}
      onClick={() => void writeClipboard(hash).then(() => setDone(true))}
    >
      {done ? "✓ copied" : `${hash.slice(0, 10)}…`}
    </button>
  );
}

export function DownloadPage() {
  const manifest = useDownloads();
  const d = useDetectedSystem();
  const { hash } = useLocation();
  // "How to install it" on the home page links to #web-app; the router does not scroll there by itself.
  useEffect(() => {
    if (!hash) return;
    // After the app's own scroll-to-top on navigation, and after the first layout.
    const t = window.setTimeout(
      () => document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" }),
      150,
    );
    return () => window.clearTimeout(t);
  }, [hash]);
  const m = manifest.data;
  const files = m?.files ?? [];
  const mine = filesFor(files, d);

  return (
    <div className="stack download">
      <PageHead title="Get StrategyHive">
        <p className="muted">
          Two ways to have StrategyHive as an app: the <strong>desktop app</strong>, which runs everything on
          your own computer and works offline, or the <strong>website installed as an app</strong>, which
          keeps your account, community and hosted bots and takes ten seconds.
        </p>
      </PageHead>

      {/* On a phone or tablet the desktop app is not an option: the web app comes first there. */}
      <div className={`download-choice${d.platform ? "" : " download-choice--web-first"}`}>
        <Card title="The desktop app">
          <div className="stack">
            <p className="small">
              Backtests and paper bots on your machine. No account, no server: the database is a file on your
              disk, and nothing leaves your computer except the market data you download. Bots run while the
              app is open (it waits in the tray).
            </p>
            {manifest.isLoading ? (
              <p className="muted small">Looking for the latest version…</p>
            ) : !m?.version ? (
              <Alert tone="error">No desktop build has been published on this server yet.</Alert>
            ) : (
              <>
                <p className="small muted">
                  Version <strong className="mono">{m.version}</strong>
                  {m.releasedAt && ` · ${new Date(m.releasedAt).toLocaleDateString()}`}
                </p>
                {d.platform && (
                  <div className="download-primary">
                    {mine.length > 0 ? (
                      mine.map((f) => (
                        <a key={f.name} className="btn btn--primary" href={f.url} download>
                          ⬇ Download for {PLATFORM_NAME[f.platform]}
                          {f.platform === "mac" ? ` (${f.arch === "arm64" ? "Apple silicon" : "Intel"})` : ""}
                          {f.platform === "linux" ? ` · ${f.kind}` : ""} · {mb(f.size)}
                        </a>
                      ))
                    ) : (
                      <p className="small">
                        {d.platform === "mac" && !d.arch
                          ? "Pick your Mac below: Apple menu → About This Mac shows the chip."
                          : `${PLATFORM_NAME[d.platform]} is not available yet; see all builds below.`}
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </Card>
        <InstallWebApp browser={d.browser} />
      </div>

      {files.length > 0 && (
        <Card title={`All downloads · version ${m!.version}`} flush>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>System</th>
                  <th>For</th>
                  <th>Type</th>
                  <th className="num">Size</th>
                  <th>SHA-256</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(["windows", "mac", "linux"] as const).flatMap((p) => {
                  const rows = files.filter((f) => f.platform === p);
                  if (rows.length === 0)
                    return [
                      <tr key={p}>
                        <td>{PLATFORM_NAME[p]}</td>
                        <td colSpan={5} className="muted small">
                          Not available yet.
                        </td>
                      </tr>,
                    ];
                  return rows.map((f) => (
                    <tr key={f.name} className={f.platform === d.platform ? "is-mine" : undefined}>
                      <td>{PLATFORM_NAME[f.platform]}</td>
                      <td className="small">{ARCH_NAME[f.arch] ?? f.arch}</td>
                      <td className="small">{KIND_NAME[f.kind] ?? f.kind}</td>
                      <td className="num">{mb(f.size)}</td>
                      <td>
                        <CopyHash hash={f.sha256} />
                      </td>
                      <td>
                        <a className="btn btn--sm" href={f.url} download>
                          Download
                        </a>
                      </td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="Installing the desktop app">
        <div className="stack">
          {!m?.signed && (
            <p className="small">
              These builds are <strong>not code-signed yet</strong>, so macOS and Windows ask once whether you
              trust them. That is expected for a beta; check the SHA-256 above if you want to be sure the file
              is the one published here.
            </p>
          )}
          <div className="install-grid">
            <section className={d.platform === "windows" ? "is-mine" : undefined}>
              <h3>Windows 10 and 11</h3>
              <ol>
                <li>
                  Open the downloaded <code>StrategyHive-…-x64.exe</code>.
                </li>
                <li>
                  If SmartScreen says “Windows protected your PC”, choose{" "}
                  <strong>More info → Run anyway</strong>.
                </li>
                <li>Pick a folder (or keep the default) and finish. No administrator rights needed.</li>
                <li>Start it from the Start menu. Uninstall it from Settings → Apps.</li>
              </ol>
            </section>
            <section className={d.platform === "mac" ? "is-mine" : undefined}>
              <h3>macOS 11 and later (Apple silicon)</h3>
              <ol>
                <li>
                  The Mac app is for Apple silicon (M1 and later; Apple menu → About This Mac shows the chip).
                  On an Intel Mac, install the website as an app instead.
                </li>
                <li>
                  Open the <code>.dmg</code> and drag StrategyHive into Applications.
                </li>
                <li>
                  The first time, <strong>right-click the app → Open → Open</strong>. On macOS 15 and later:
                  try to open it once, then System Settings → Privacy &amp; Security →{" "}
                  <strong>Open Anyway</strong>.
                </li>
                <li>Uninstall by dragging it from Applications to the Bin.</li>
              </ol>
            </section>
            <section className={d.platform === "linux" ? "is-mine" : undefined}>
              <h3>Linux</h3>
              <ol>
                <li>
                  <strong>.deb</strong> (Debian, Ubuntu, Mint):{" "}
                  <code>sudo apt install ./StrategyHive-…-amd64.deb</code>, then start it from the
                  applications menu.
                </li>
                <li>
                  <strong>AppImage</strong> (any distribution):{" "}
                  <code>chmod +x StrategyHive-…-x86_64.AppImage</code> and run it. On Ubuntu 22.04 and later
                  install <code>libfuse2</code> first.
                </li>
              </ol>
            </section>
          </div>
          <p className="small muted">
            Updates are manual for now: download the new version from this page and install it over the old
            one. Your strategies and bots are kept. More in the <Link to="/docs/desktop">Docs</Link>.
          </p>
        </div>
      </Card>

      <Card title="Which one should I use?" flush>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th />
                <th>Desktop app</th>
                <th>Website as an app</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Account", "None", "Your StrategyHive account"],
                ["Works offline", "Yes", "No"],
                ["Paper bots keep running", "While the app is open", "Always, on the server"],
                ["Explore, League, follows", "No", "Yes"],
                ["Notifications", "From the computer", "Push to phone and computer"],
                ["Phones and tablets", "No", "Yes"],
                ["Install", "Download (~100 MB)", "Ten seconds, nothing to download"],
              ].map(([k, a, b]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td>{a}</td>
                  <td>{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/** The website as an installed app: one button where the browser allows it, steps where it does not. */
function InstallWebApp({ browser }: { browser: Browser }) {
  const { state, install } = useInstallState();
  const [outcome, setOutcome] = useState<string | null>(null);
  const steps: Record<Browser, { title: string; steps: string[] }> = {
    chromium: {
      title: "Chrome or Edge on a computer",
      steps: [
        "Click the install icon at the right of the address bar (a screen with an arrow), or",
        "open the browser menu ⋮ → Cast, save and share → Install StrategyHive (Edge: Apps → Install this site as an app).",
      ],
    },
    android: {
      title: "Android (Chrome)",
      steps: [
        "Tap the menu ⋮ → Install app (or Add to Home screen).",
        "Open StrategyHive from your home screen.",
      ],
    },
    ios: {
      title: "iPhone or iPad (Safari)",
      steps: [
        "Tap the Share button (a square with an arrow).",
        "Scroll down and tap Add to Home Screen, then Add.",
        "Open StrategyHive from the Home Screen. Push notifications on iPhone and iPad only work this way.",
      ],
    },
    safari: {
      title: "Safari on a Mac (macOS 14 and later)",
      steps: ["Choose File → Add to Dock.", "StrategyHive opens in its own window from the Dock."],
    },
    firefox: {
      title: "Firefox",
      steps: [
        "Firefox does not install websites as apps on most systems.",
        "Pin the tab (right-click it → Pin Tab), or open this page in Chrome, Edge or Safari to install.",
      ],
    },
    other: {
      title: "Your browser",
      steps: ["Look for “Install app” or “Add to Home Screen” in the browser menu."],
    },
  };
  const here = steps[browser];
  return (
    <div id="web-app" className="download-anchor">
      <Card title="The website, installed as an app">
        <div className="stack">
          <p className="small">
            Your account, Explore, the League and your hosted bots, in its own window with its own icon, and
            push notifications on your phone. Nothing to download; it always runs the latest version.
          </p>
          {state === "installed" ? (
            <Alert tone="ok">You are using StrategyHive as an installed app.</Alert>
          ) : state === "available" ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => void install().then(setOutcome)}
            >
              ⤓ Install StrategyHive
            </button>
          ) : null}
          {outcome === "dismissed" && (
            <p className="small muted">No problem; the steps below work any time.</p>
          )}
          <div className={`install-steps${state === "available" ? " is-secondary" : ""}`}>
            <h3>{here.title}</h3>
            <ol>
              {here.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          </div>
          <details>
            <summary className="small">Other browsers and devices</summary>
            <div className="stack">
              {(Object.keys(steps) as Browser[])
                .filter((b) => b !== browser && b !== "other")
                .map((b) => (
                  <div key={b} className="install-steps">
                    <h3>{steps[b].title}</h3>
                    <ol>
                      {steps[b].steps.map((s) => (
                        <li key={s}>{s}</li>
                      ))}
                    </ol>
                  </div>
                ))}
            </div>
          </details>
        </div>
      </Card>
    </div>
  );
}
